import React, { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import {
    ThemeProvider,
    Link,
    RowValuesType,
    Checkbox,
    CheckboxStateValues,
} from '@cxc/react-fpds-components';
import { defaultTheme } from '@cxc/react-fpds-foundation';
import { useUniversalContext } from '@cxc/react-universal-components';
import Header from '../../components/Header/Header';
import InputFieldsContainer from '../../components/InputFieldsContainer/InputFieldsContainer';
import Table from '../../components/Table/Table';
import Modal from '../../components/Modal/Modal';
import SkeletonTable from '../../components/SkeletonTable/SkeletonTable';
import CircleIcon from '../../components/Icons/CircleIcon';
import TableColumnConstants from '../../constants/TableColumnConstants';
import { IncentiveApiData } from '../../types/IncentivesDataType';
import { WeightClass } from '../../types/WeightClass';
import { formatCapitalizeFirstLetters, getColorBasedOnStatus } from '../../utils/utils';
import { constructDigitalData, fireAnalyticsEvent } from '../../analytics/analytics';
import { CarFront, ChargeStatusCharging } from '@cxc/react-fpds-icons-v1';
import {
    incentivesData,
    filteredIncentivesData,
    filteredIncentivesDataIndex,
    organizationType,
    equipmentType,
    status,
    showModal,
    weightClassData,
    incentiveType,
    chargerType,
    selectedIncentivesData,
    zipCode,
} from '../../state/GlobalState';
import {
    LANDING_PAGE_NAME,
    GLOBAL_LOAD,
    FPG_COOKIES,
    COOKIE_PREFERENCES,
} from '../../analytics/analyticsConstants';
import { EQUIPMENT_TYPE_CHARGER, EQUIPMENT_TYPE_VEHICLE } from '../../constants/EquipmentConstants';
import { DETAILS_PAGE_ROUTE, FPG_PAGE_ROUTE } from '../../constants/Routes';
import { DropdownConstants } from '../../constants/DropdownConstants';
import { ButtonPlacementType } from '../../constants/SummaryConstants';
import { difference, includes } from 'lodash';
import { IncentiveTypes } from '../../constants/IncentiveTypes';
import { LANDING_PAGE_MODAL_DATA } from '../../constants/LandingPageConstants';
import ResidentialIncentivesTableView from '../ResidentialIncentivesTableView/ResidentialIncentivesTableView';

const itemStyles = {
    paddingBottom: '48px',
};

const LandingPage = () => {
    const [isOpen, setIsOpen] = useAtom<boolean>(showModal);
    const [selectedStatus] = useAtom<string>(status);
    const [selectedOrgType] = useAtom<string>(organizationType);
    const [selectedEquipment] = useAtom<string>(equipmentType);
    const [selectedWeightClass] = useAtom<WeightClass[]>(weightClassData);
    const [selectedIncentiveType] = useAtom<string>(incentiveType);
    const [selectedChargerType] = useAtom<string>(chargerType);
    const { navigate } = useUniversalContext();
    const [incentives] = useAtom<IncentiveApiData[]>(incentivesData);
    const [filteredIncentives, setFilteredIncentives] =
        useAtom<IncentiveApiData[]>(filteredIncentivesData);
    const [, setIndex] = useAtom<number>(filteredIncentivesDataIndex);
    const [isLoading, setIsLoading] = useState(false);
    const { isLoggedIn, subscribeScopedValue } = useUniversalContext();
    const cookiePreferences = subscribeScopedValue(FPG_COOKIES, COOKIE_PREFERENCES);
    const [selectedIncentives, setSelectedIncentives] = useAtom(selectedIncentivesData);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [zipcode] = useAtom(zipCode);
    useEffect(() => {
        const checkAdobeLaunchInterval = setInterval(() => {
            if (window._satellite && typeof window._satellite === 'object') {
                clearInterval(checkAdobeLaunchInterval);
                const digitalData = constructDigitalData(
                    LANDING_PAGE_NAME,
                    GLOBAL_LOAD,
                    isLoggedIn,
                    cookiePreferences,
                );
                fireAnalyticsEvent(digitalData.digitalData, digitalData.directCallName);
            }
        }, 100);
    }, [isLoggedIn, cookiePreferences]);

    const renderEligibleEquipment = (equipmentList: string[]) => {
        return equipmentList.map((equipment) => {
            if (equipment === EQUIPMENT_TYPE_CHARGER) {
                return <ChargeStatusCharging key={equipment} size={'s'} />;
            } else {
                return <CarFront key={equipment} size={'s'} />;
            }
        });
    };

    useEffect(() => {
        if (incentives && incentives.length > 0) {
            let filteredIncentiveData = incentives.filter((row) => {
                return (
                    (selectedStatus === DropdownConstants.ALL ||
                        row.currentProgramStatus === selectedStatus) &&
                    (selectedOrgType === DropdownConstants.ALL ||
                        row.eligibleOrganizations.includes(selectedOrgType)) &&
                    (selectedEquipment === DropdownConstants.ALL ||
                        row.eligibleEquipment.includes(selectedEquipment)) &&
                    (selectedChargerType === DropdownConstants.ALL ||
                        (row.eligibleChargingTechnology !== null &&
                            row.eligibleChargingTechnology.includes(selectedChargerType)))
                );
            });

            if (selectedEquipment === EQUIPMENT_TYPE_VEHICLE && selectedWeightClass.length > 0) {
                const selectedWeightClassValues = [
                    ...new Set(
                        selectedWeightClass.map((weightClass) => {
                            return weightClass.value?.replace('repeated', '');
                        }),
                    ),
                ];
                filteredIncentiveData = filteredIncentiveData.filter((incentive) => {
                    const presentItems = selectedWeightClassValues.filter((item) => {
                        return includes(incentive.eligibleVehicleWeightClasses?.split(', '), item);
                    });
                    return presentItems.length > 0;
                });
            }
            setFilteredIncentives(filteredIncentiveData);
        }
    }, [
        selectedStatus,
        selectedOrgType,
        selectedEquipment,
        incentives,
        selectedWeightClass,
        selectedChargerType,
        setFilteredIncentives,
    ]);

    const [prevDropdownValues, setPrevDropdownValues] = useState({
        status: selectedStatus,
        orgType: selectedOrgType,
        equipment: selectedEquipment,
        weightClass: selectedWeightClass,
        chargerType: selectedChargerType,
        zipcode: zipcode,
    });

    useEffect(() => {
        const hasChanged =
            prevDropdownValues.status !== selectedStatus ||
            prevDropdownValues.orgType !== selectedOrgType ||
            prevDropdownValues.equipment !== selectedEquipment ||
            prevDropdownValues.weightClass !== selectedWeightClass ||
            prevDropdownValues.chargerType !== selectedChargerType ||
            prevDropdownValues.zipcode !== zipcode;
        if (hasChanged) {
            setSelectedIncentives([]);
            setPrevDropdownValues({
                status: selectedStatus,
                orgType: selectedOrgType,
                equipment: selectedEquipment,
                weightClass: selectedWeightClass,
                chargerType: selectedChargerType,
                zipcode: zipcode,
            });
        }
    }, [
        selectedStatus,
        selectedOrgType,
        selectedEquipment,
        selectedWeightClass,
        selectedChargerType,
        setSelectedIncentives,
        zipcode,
        prevDropdownValues,
    ]);

    const getCurrentPageItems = () => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredIncentives.slice(startIndex, endIndex);
    };

    const handleCheckboxChange =
        (incentive: IncentiveApiData) => (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.checked) {
                setSelectedIncentives([...selectedIncentives, incentive]);
            } else {
                setSelectedIncentives(selectedIncentives.filter((row) => row !== incentive));
            }
        };

    const handleSelectAllChange = (isChecked: boolean) => {
        const currentPageItems = getCurrentPageItems();
        if (!isChecked) {
            setSelectedIncentives(
                selectedIncentives.filter((incentive) => !currentPageItems.includes(incentive)),
            );
        } else {
            setSelectedIncentives([...new Set([...selectedIncentives, ...currentPageItems])]);
        }
    };

    const getCheckboxValue = (incentive: IncentiveApiData) => {
        return selectedIncentives.indexOf(incentive) === -1
            ? CheckboxStateValues.UNCHECKED
            : CheckboxStateValues.CHECKED;
    };

    const handleItemsPerPageChange = (items: number) => {
        setItemsPerPage(items);
        setCurrentPage(1);
    };

    const isAllChecked = () => {
        const currentPageItems = getCurrentPageItems();
        const isChecked = difference(currentPageItems, selectedIncentives);
        if (selectedIncentives.length) {
            return isChecked.length === 0
                ? CheckboxStateValues.CHECKED
                : CheckboxStateValues.UNCHECKED;
        } else {
            return CheckboxStateValues.UNCHECKED;
        }
    };

    const mapIncentiveDataResponseToTableFormat = (incentiveData: IncentiveApiData[]) => {
        const incentiveTableData: RowValuesType = [];
        incentiveData.forEach((incentive: IncentiveApiData, index: number) => {
            incentiveTableData.push({
                rowId: incentive.incentiveName,
                cellData: [
                    {
                        leadingComponents: (
                            <Checkbox
                                value="default"
                                checkboxValue={getCheckboxValue(incentive)}
                                onChange={handleCheckboxChange(incentive)}
                            />
                        ),
                    },
                    {
                        leadingComponents: (
                            <Link
                                onClick={() => {
                                    setIndex(index);
                                    navigate(`${FPG_PAGE_ROUTE}${DETAILS_PAGE_ROUTE}`);
                                }}
                                size="medium"
                            >
                                <div style={{ cursor: 'pointer', color: '#0564D7' }}>
                                    {incentive.incentiveName}
                                </div>
                            </Link>
                        ),
                    },
                    {
                        textValue: formatCapitalizeFirstLetters(incentive.currentProgramStatus),
                        leadingComponents: (
                            <CircleIcon
                                size="8px"
                                color={getColorBasedOnStatus(incentive.currentProgramStatus)}
                            />
                        ),
                    },
                    {
                        description: incentive.potentialValue,
                    },
                    {
                        description: incentive.grantorName,
                    },
                    {
                        leadingComponents: renderEligibleEquipment(incentive.eligibleEquipment),
                    },
                    {
                        description: formatCapitalizeFirstLetters(incentive.incentiveNature),
                    },
                ],
            });
        });
        return incentiveTableData;
    };

    const renderIncentivesTable = () => {
        if (isLoading) {
            return <SkeletonTable style={itemStyles} numberOfRows={3} />;
        }
        if (selectedIncentiveType === IncentiveTypes.COMMERCIAL && incentives.length > 0) {
            return (
                <>
                    <Table
                        headerValues={[
                            {
                                id: 'select-checkbox',
                                leadingComponents: (
                                    <Checkbox
                                        value="select-all"
                                        checkboxValue={isAllChecked()}
                                        onChange={(e) => handleSelectAllChange(e.target.checked)}
                                    />
                                ),
                                contentAlignment: 'left',
                            },
                            ...TableColumnConstants,
                        ]}
                        rowValues={mapIncentiveDataResponseToTableFormat(filteredIncentives)}
                        paginationProps={{
                            isPaginationEnabled: true,
                            tablePage: currentPage,
                            itemsPerPage: 10,
                            totalItems: filteredIncentives.length,
                            setTablePage: (page) => setCurrentPage(page),
                            setItemsPerPage: handleItemsPerPageChange,
                        }}
                    />
                </>
            );
        } else if (selectedIncentiveType === IncentiveTypes.RESIDENTIAL) {
            return <ResidentialIncentivesTableView />;
        } else {
            return null;
        }
    };

    return (
        <ThemeProvider theme={defaultTheme}>
            <Modal
                title={LANDING_PAGE_MODAL_DATA.TITLE}
                buttons={LANDING_PAGE_MODAL_DATA.BUTTONS}
                buttonPlacement={ButtonPlacementType.right}
                description={LANDING_PAGE_MODAL_DATA.DESCRIPTION}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                headingLevel={'h1'}
                variant={'large'}
                url={''}
            />
            <Header />
            <InputFieldsContainer isLoading={isLoading} setIsLoading={setIsLoading} />
            {renderIncentivesTable()}
        </ThemeProvider>
    );
};

export default LandingPage;
